"use client";

import React from "react";
import { Activity, CircleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Cpu, Droplets, Gauge, Layers, Settings2, ShieldCheck, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const features = [{
  icon: Droplets,
  title: "Dosagem precisa e uniforme",
  description: "Aplicação controlada de óleos e desmoldantes diretamente nos pontos de atrito sem névoa excessiva nem desperdício de lubrificante."
}, {
  icon: Activity,
  title: "Manutenção preditiva e fiabilidade",
  description: "Redução drástica do desgaste prematuro em correntes, rolamentos e esteiras com ciclos de lubrificação automáticos programáveis."
}, {
  icon: Zap,
  title: "Eficiência energética e operacional",
  description: "Menor resistência mecânica e redução de paragens não planeadas nas linhas de produção contínua."
}, {
  icon: ShieldCheck,
  title: "Segurança e conformidade ambiental",
  description: "Eliminação de derrames e vapores no chão de fábrica, assegurando postos de trabalho mais limpos e seguros."
}];
const technicalSpecs = [{
  label: "Redução de consumo de lubrificante",
  value: "Até 50%",
  detail: "Face aos métodos manuais ou por imersão convencionais"
}, {
  label: "Aumento da vida útil das correntes",
  value: "3x a 5x",
  detail: "Com aplicação direta no passo da corrente e elos de articulação"
}, {
  label: "Tempo de paragem reduzido",
  value: "-70%",
  detail: "Eliminação de rotinas manuais com a linha de produção em marcha"
}, {
  label: "Precisão de disparo",
  value: "Milissegundos",
  detail: "Sincronização ótica com bicos de comando eletromagnético PulsaJet"
}];
const applications = [{
  title: "Linhas de transporte e correntes",
  description: "Lubrificação gota a gota ou microaspersão em fornos, estufas e túneis de congelação."
}, {
  title: "Conformação e estampagem",
  description: "Aplicação uniforme de desmoldantes e óleos de corte antes da prensa mecânica."
}, {
  title: "Rolamentos e guias lineares",
  description: "Injeção controlada de óleo ou massa sintética em zonas de acesso restrito e alta carga."
}, {
  title: "Indústria alimentar e embalagem",
  description: "Sistemas certificados NSF/FDA para esteiras de transporte de garrafas, latas e caixas."
}];
export default function Lubrication() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="lubricationoverview" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Sistemas de Engenharia & Lubrificação
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-default">
            Lubrificação de precisão para máxima disponibilidade industrial
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">Um equívoco comum é que “quanto mais, melhor” na aplicação de lubrificantes. Não só isto é falso, como conduz a um aumento desnecessário de custos e pode também criar riscos evitáveis. Demasiado lubrificante pode originar escorrimento, resíduos, fumo, riscos de escorregamento e contaminação a jusante. Por outro lado, a aplicação de demasiado pouco traz o seu próprio conjunto de desafios. Quando a película é insuficiente, pode causar atrito, desgaste e danos no produto.</p>
        </div>

        {/* Hero Highlights Grid */}
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <motion.div key={feature.title} data-index={i} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeIn} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-secondary/40 hover:shadow-md">
                  <CardHeader>
                    <div className="flex size-12 items-center justify-center rounded-lg bg-accent text-accent-foreground mb-2">
                      <Icon className="size-6 text-secondary" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Detailed Showcase Section */}
        <div className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.3
        }} variants={fadeIn} className="lg:col-span-6 flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 text-secondary font-medium text-sm">
              <Cpu className="size-4" />
              <span>Tecnologia AutoJet e bicos PulsaJet</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default">
              Controlo de pulverização com precisão milimétrica
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              O sistema de lubrificação automatizado sincroniza o fluxo do fluido com a velocidade da linha. Ao usar modulação de largura de pulso (PWM), ajusta o caudal instantaneamente sem alterar a pressão hidráulica nem o padrão de pulverização, garantindo uma camada homogénea em cada ciclo.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">Sem formação de névoa no ar</span>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">Integração com PLC de fábrica</span>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">Sensores de fluxo e pressão</span>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">Disponível em aço inoxidável 316</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactsPage">
                  Solicitar diagnóstico técnico
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/AutomatedSprayingSystemsPage">
                  Explorar sistemas AutoJet
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.3
        }} variants={fadeIn} className="lg:col-span-6">
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-md">
              <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/lubrication_system_controller.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/lubrication_system_controller.png-wvc-sizes" alt="Sistema automatizado de lubrificação industrial Eurocontrol" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover" src="https://eurocontrol.pt/wp-content/uploads/2026/09/Captura-de-ecra-2026-09-08-as-14.52.59.png" />
              <div className="p-6 bg-card border-t border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-foreground text-sm">Controlador AutoJet®&nbsp;</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Painel integrado com dosagem de ciclo fechado</p>
                  </div>
                  <Badge variant="secondary" className="font-mono text-xs">
                    Spraying Systems Co.
                  </Badge>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Operational Efficiency Metric Strip */}
        <div className="mt-16 lg:mt-24 rounded-2xl border border-border bg-muted/40 p-6 sm:p-10">
          <div className="max-w-2xl mb-8">
            <h4 className="text-xl font-bold tracking-tight text-foreground font-default">
              Impacto no custo total de propriedade (TCO)
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              Resultados médios obtidos em clientes industriais em Portugal após a modernização dos circuitos de lubrificação.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {technicalSpecs.map((spec, i) => <div key={spec.label} data-index={i} className="rounded-xl bg-card p-5 border border-border/80 shadow-xs">
                <span className="text-2xl sm:text-3xl font-bold text-primary tracking-tight font-mono block">
                  {spec.value}
                </span>
                <span className="text-sm font-semibold text-foreground mt-1 block">
                  {spec.label}
                </span>
                <span className="text-xs text-muted-foreground mt-1.5 block leading-normal">
                  {spec.detail}
                </span>
              </div>)}
          </div>
        </div>

        {/* Common Industrial Applications */}
        <div className="mt-16 lg:mt-24">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <h4 className="text-2xl font-bold tracking-tight text-foreground font-default">
                Aplicações frequentes por setor
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                Soluções testadas nas indústrias automóvel, alimentar, metalomecânica e papel em Portugal.
              </p>
            </div>
            <Button asChild variant="ghost" className="self-start sm:self-auto text-secondary">
              <Link to="/ApplicationsPage">
                Ver todos os setores
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {applications.map((app, i) => <div key={app.title} data-index={i} className="flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-xs transition-[border-color] duration-200 hover:border-secondary/30">
                <div>
                  <div className="size-8 rounded-md bg-secondary/10 text-secondary flex items-center justify-center mb-3">
                    <Settings2 className="size-4" />
                  </div>
                  <h5 className="font-semibold text-foreground text-base mb-2">
                    {app.title}
                  </h5>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {app.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* CTA Bar */}
        <div className="mt-16 rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 shadow-lg flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-xl text-center md:text-left">
            <h4 className="text-2xl sm:text-3xl font-bold font-default text-primary-foreground">
              Pretende otimizar a lubrificação das suas linhas?
            </h4>
            <p className="mt-2 text-primary-foreground/80 text-sm sm:text-base">
              Os nossos engenheiros realizam auditorias técnicas no local e calculam a poupança prevista em consumíveis e manutenção.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="secondary" size="lg" className="shadow-md">
              <Link to="/ContactsPage">
                Falar com um especialista
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/CatalogsPage">
                Consultar catálogo
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}